import { useState, useCallback, useMemo, useRef } from "react";
import { createUpload, completeUpload } from "../../../services/uploadFilesService";
import { uploadPartsWithConcurrency, uploadSrtToS3 } from "../../../utils/multipartUpload";
import { generateSrtFiles } from "../../../services/generateSrtService";

import {
  ACCEPTED_VIDEO_TYPES,
  ACCEPTED_SRT_TYPES,
  MAX_VIDEO_SIZE_BYTES,
  MAX_SRT_SIZE_BYTES,
  UPLOAD_STATUS,
  SYNC_TRIGGER_STATUS,
  UPLOAD_ERROR,
} from "../../../constants";

// Animates a progress value from 0 → 100 over ~durationMs milliseconds.
// Returns a Promise that resolves when the animation is done.
const _animateProgress = (setFn, durationMs = 1200) =>
  new Promise((resolve) => {
    const steps = 20;
    const stepMs = durationMs / steps;
    const stepSize = 100 / steps;
    let current = 0;
    const id = setInterval(() => {
      current += stepSize;
      setFn(Math.min(Math.round(current), 100));
      if (current >= 100) {
        clearInterval(id);
        resolve();
      }
    }, stepMs);
  });

export {
  ACCEPTED_VIDEO_TYPES,
  ACCEPTED_SRT_TYPES,
  MAX_VIDEO_SIZE_BYTES,
  MAX_SRT_SIZE_BYTES,
  UPLOAD_STATUS,
  SYNC_TRIGGER_STATUS,
  UPLOAD_ERROR,
};

export const validateFile = (file, kind) => {
  if (!file) return null;

  if (kind === "video") {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "mp4" && !ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      return "Only MP4 files are accepted for the video.";
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      return "Video must be smaller than 1 GB.";
    }
  }

  if (kind === "srt") {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "srt") {
      return "Only SRT subtitle files are accepted.";
    }
    if (file.size > MAX_SRT_SIZE_BYTES) {
      return "SRT file must be smaller than 50 KB.";
    }
  }

  return null;
};

export function useFileUpload(serviceConfig = {}) {
  const { serviceId = "polly", languages = [], voices = {}, userEmail = "" } = serviceConfig;

  const [videoFile, setVideoFile] = useState(null);
  const [videoError, setVideoError] = useState("");
  const [originalSrtFile, setOriginalSrtFile] = useState(null);
  const [originalSrtError, setOriginalSrtError] = useState("");

  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
  const [videoProgress, setVideoProgress] = useState(0); // 0–100
  const [srtProgress, setSrtProgress] = useState(0); // 0–100
  const [uploadError, setUploadError] = useState("");

  const [jobId, setJobId] = useState(null);
  const [videoKey, setVideoKey] = useState(null);
  const [srtKey, setSrtKey] = useState(null);
  const [syncEnabled, setSyncEnabled] = useState(false);

  const [syncTriggerStatus, setSyncTriggerStatus] = useState(SYNC_TRIGGER_STATUS.IDLE);
  const [syncTriggerError, setSyncTriggerError] = useState("");

  const abortControllerRef = useRef(null);

  const pollyMapping = useMemo(
    () =>
      languages
        .filter((lang) => voices[lang])
        .map((lang) => ({
          language: lang,
          voice_id: voices[lang].name?.normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
          engine: voices[lang].engine,
          gender: voices[lang].gender,
        })),
    [languages, voices]
  );

  const hasVideo = Boolean(videoFile);
  const hasOriginalSrt = Boolean(originalSrtFile);

  // Filename match check (without extension) — e.g. "video.mp4" & "video.srt"
  const videoBaseName = videoFile
    ? videoFile.name
        .replace(/\.[^/.]+$/, "")
        .trim()
        .normalize("NFC")
    : null;
  const srtBaseName = originalSrtFile
    ? originalSrtFile.name
        .replace(/\.[^/.]+$/, "")
        .trim()
        .normalize("NFC")
    : null;
  const fileNamesMatch = videoBaseName && srtBaseName ? videoBaseName === srtBaseName : true;

  const isFormValid = hasVideo && hasOriginalSrt && fileNamesMatch;
  const hasAnyError = Boolean(videoError || originalSrtError);

  const handleVideoSelect = useCallback((file) => {
    const err = validateFile(file, "video");
    setVideoError(err || "");
    setVideoFile(err ? null : file);

    if (file) {
      // eslint-disable-next-line no-console
      console.log("[Video Metadata]", {
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
        type: file.type,
        lastModified: new Date(file.lastModified).toISOString(),
      });
    }
  }, []);

  const handleOriginalSrtSelect = useCallback((file) => {
    const err = validateFile(file, "srt");
    setOriginalSrtError(err || "");
    setOriginalSrtFile(err ? null : file);
  }, []);

  const clearVideo = useCallback(() => {
    setVideoFile(null);
    setVideoError("");
  }, []);

  const clearOriginalSrt = useCallback(() => {
    setOriginalSrtFile(null);
    setOriginalSrtError("");
  }, []);

  const startUpload = useCallback(async () => {
    if (!isFormValid) return;

    // Fresh AbortController for this run
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    // Reset all progress state
    setUploadStatus(UPLOAD_STATUS.UPLOADING);
    setVideoProgress(0);
    setSrtProgress(0);
    setUploadError("");
    setSyncEnabled(false);
    setJobId(null);
    setVideoKey(null);
    setSrtKey(null);

    try {
      // eslint-disable-next-line no-console
      console.log("[useFileUpload] Phase 1 — create_upload");
      const session = await createUpload({
        videoFile,
        srtFile: originalSrtFile,
        serviceType: serviceId,
        pollyMapping,
        userEmail,
      });

      if (signal.aborted) throw new Error(UPLOAD_ERROR.CANCELLED);

      const {
        jobId: jId,
        uploadId,
        videoKey: vKey,
        srtKey: sKey,
        partUrls,
        srtUrl,
        partSize,
      } = session;

      // eslint-disable-next-line no-console
      console.log(`[useFileUpload] Phase 1 — uploading ${partUrls.length} video part(s) + SRT`);

      const [completedParts] = await Promise.all([
        uploadPartsWithConcurrency({
          videoFile,
          partUrls,
          partSize,
          onProgress: setVideoProgress,
          signal,
        }),
        uploadSrtToS3(originalSrtFile, srtUrl, setSrtProgress, signal),
      ]);

      if (signal.aborted) throw new Error(UPLOAD_ERROR.CANCELLED);

      // eslint-disable-next-line no-console
      console.log("[useFileUpload] Phase 2 — complete_upload");
      setUploadStatus(UPLOAD_STATUS.VERIFYING);

      const result = await completeUpload({
        jobId: jId,
        uploadId,
        videoKey: vKey,
        srtKey: sKey,
        completedParts,
      });

      if (result.uploadStatus === "SUCCESS" && result.syncEnabled === true) {
        // eslint-disable-next-line no-console
        console.log("[useFileUpload] ✅ Upload complete — jobId:", jId);

        setJobId(jId);
        setVideoKey(vKey);
        setSrtKey(sKey);
        setSyncEnabled(true);
        setVideoProgress(100);
        setSrtProgress(100);
        setUploadStatus(UPLOAD_STATUS.UPLOAD_COMPLETE);
      } else {
        throw new Error(result.error || "Upload verification failed.");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[useFileUpload] ❌", err.message);

      let userMessage;
      if (err.message === UPLOAD_ERROR.SESSION_EXPIRED) {
        userMessage = "Your session has expired. Please log in again.";
      } else if (
        err.message === UPLOAD_ERROR.DUPLICATE_JOB ||
        // Also catch the typed error string returned from the Lambda 409 body
        err.message?.toLowerCase().includes("already exists") ||
        err.message?.toLowerCase().includes("duplicate")
      ) {
        userMessage =
          "A job for this video file already exists. " +
          "Please rename the file (e.g. add a version suffix) and try again, " +
          "or wait for the existing job to complete.";
      } else if (err.message === UPLOAD_ERROR.NETWORK_ERROR) {
        userMessage = "A network error occurred. Please check your connection and try again.";
      } else if (err.message === UPLOAD_ERROR.CANCELLED) {
        userMessage = "Upload was cancelled.";
      } else if (err.message?.includes("invalid presigned URL")) {
        userMessage =
          "Upload configuration error: the server returned an invalid upload URL. " +
          "Please contact support.";
      } else {
        userMessage = err.message || "Upload failed. Please try again.";
      }

      setUploadError(userMessage);
      setUploadStatus(UPLOAD_STATUS.UPLOAD_FAILED);
    }
  }, [isFormValid, videoFile, originalSrtFile, serviceId, pollyMapping, userEmail]);

  const cancelUpload = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const triggerSync = useCallback(async () => {
    if (uploadStatus !== UPLOAD_STATUS.UPLOAD_COMPLETE || !syncEnabled || !jobId) {
      const reason = !jobId
        ? "No job ID found. Please upload files first."
        : !syncEnabled
          ? "Files have not been verified yet. Please wait for upload to complete."
          : "Upload is not complete. Please wait for verification.";
      setSyncTriggerError(reason);
      setSyncTriggerStatus(SYNC_TRIGGER_STATUS.FAILED);
      throw new Error(reason);
    }

    setSyncTriggerStatus(SYNC_TRIGGER_STATUS.TRIGGERING);
    setSyncTriggerError("");

    try {
      // eslint-disable-next-line no-console
      console.log("[useFileUpload] triggerSync — generate_srt_files, jobId:", jobId);
      await generateSrtFiles(jobId);
      // eslint-disable-next-line no-console
      console.log("[useFileUpload] ✅ SRT generation triggered — jobId:", jobId);
      setSyncTriggerStatus(SYNC_TRIGGER_STATUS.TRIGGERED);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[useFileUpload] ❌ generate_srt_files failed:", err.message);
      const message =
        "Synchronisation could not be started: " +
        (err.message || "Unknown error.") +
        " Please try again or contact support.";
      setSyncTriggerError(message);
      setSyncTriggerStatus(SYNC_TRIGGER_STATUS.FAILED);
      throw new Error(message);
    }
  }, [uploadStatus, syncEnabled, jobId]);

  const resetUpload = useCallback(() => {
    abortControllerRef.current?.abort();
    setUploadStatus(UPLOAD_STATUS.IDLE);
    setVideoProgress(0);
    setSrtProgress(0);
    setUploadError("");
    setSyncEnabled(false);
    setJobId(null);
    setVideoKey(null);
    setSrtKey(null);
    setSyncTriggerStatus(SYNC_TRIGGER_STATUS.IDLE);
    setSyncTriggerError("");
  }, []);

  return {
    // File state
    videoFile,
    videoError,
    handleVideoSelect,
    clearVideo,
    originalSrtFile,
    originalSrtError,
    handleOriginalSrtSelect,
    clearOriginalSrt,
    // Derived payload info
    pollyMapping,
    // Validation
    isFormValid,
    hasAnyError,
    hasVideo,
    hasOriginalSrt,
    // Upload state
    uploadStatus,
    videoProgress,
    srtProgress,
    uploadError,
    // Job metadata (for Sync page)
    jobId,
    videoKey,
    srtKey,
    syncEnabled,
    // Actions
    startUpload,
    cancelUpload,
    resetUpload,
    // Sync trigger
    triggerSync,
    syncTriggerStatus,
    syncTriggerError,
  };
}
